import { memo, useMemo } from "react";

import { Column, TagTextAlert } from "@bridgesplit/ui";
import {
    BsMetaUtil,
    getLoanStrategyIdentifier,
    getZcCollateral,
    getZcLedger,
    useUserAvailableAssets,
    useWhirlpoolIncreaseLiquidityTransaction
} from "@bridgesplit/abf-react";
import { decimalsToBps, LOADING_ERROR, Result } from "@bridgesplit/utils";
import { GetUpdateLiquidityTxnArgs } from "@bridgesplit/abf-sdk";

import { AppButton } from "../../../../common";
import { useDepositWhirlpoolBalances } from "./util";
import { useWhirlpoolContext } from "../WhirlpoolContext";
import { useTransactionSender } from "../../../../transactions";
import { getMaxBalanceBalanceWarning } from "../../util";

export default memo(function DepositCta() {
    const {
        depositForm,
        poolData,
        addLiquidityQuote,
        whirlpoolPosition,
        slippageController: { isAutoSlippage }
    } = useWhirlpoolContext();
    const { A, B } = useDepositWhirlpoolBalances();
    const addLiquidity = useAddLiquidity();
    const { data: userAssets } = useUserAvailableAssets({});

    const rawUserTokenAAmount = useMemo(() => {
        return userAssets?.find((asset) => asset.metadata.mint === whirlpoolPosition.tokenA.metadata.mint)?.amount ?? 0;
    }, [userAssets, whirlpoolPosition.tokenA.metadata.mint]);

    const rawUserTokenBAmount = useMemo(() => {
        return userAssets?.find((asset) => asset.metadata.mint === whirlpoolPosition.tokenB.metadata.mint)?.amount ?? 0;
    }, [userAssets, whirlpoolPosition.tokenB.metadata.mint]);

    const alertError = useMemo(() => {
        // don't show alert with no user input
        if (depositForm.status === undefined) return undefined;
        if (A.insufficientBalance)
            return `Insufficient ${BsMetaUtil.getSymbol(whirlpoolPosition.tokenA.metadata)} balance`;
        if (B.insufficientBalance)
            return `Insufficient ${BsMetaUtil.getSymbol(whirlpoolPosition.tokenB.metadata)} balance`;

        return undefined;
    }, [
        depositForm.status,
        A.insufficientBalance,
        whirlpoolPosition.tokenA.metadata,
        whirlpoolPosition.tokenB.metadata,
        B.insufficientBalance
    ]);

    const ctaError = useMemo(() => {
        if (!depositForm.tokenAAmount && !depositForm.tokenBAmount) return "Enter an amount";
        if (depositForm.status === undefined) return "No changes made";
        if (depositForm.status === "error") return "No quote found";
        if (!poolData || !addLiquidityQuote) return "Loading";
        return undefined;
    }, [depositForm.tokenAAmount, depositForm.tokenBAmount, depositForm.status, poolData, addLiquidityQuote]);

    const ctaWarning = useMemo(() => {
        return getMaxBalanceBalanceWarning(
            isAutoSlippage,
            whirlpoolPosition.tokenA.metadata,
            whirlpoolPosition.tokenB.metadata,
            depositForm.maxTokenAAmount,
            depositForm.maxTokenBAmount,
            rawUserTokenAAmount,
            rawUserTokenBAmount
        );
    }, [
        depositForm.maxTokenAAmount,
        depositForm.maxTokenBAmount,
        isAutoSlippage,
        rawUserTokenAAmount,
        rawUserTokenBAmount,
        whirlpoolPosition.tokenA.metadata,
        whirlpoolPosition.tokenB.metadata
    ]);

    return (
        <Column spacing={1}>
            {!!alertError && (
                <TagTextAlert color="error" icon="warning">
                    {alertError}
                </TagTextAlert>
            )}
            {!alertError && !!ctaWarning && (
                <TagTextAlert color="warning" icon="warning">
                    {ctaWarning}
                </TagTextAlert>
            )}
            <AppButton
                asyncCta={{ onClickWithResult: addLiquidity }}
                isTransaction
                color="secondary"
                disabled={!!ctaError || !!alertError}
            >
                {ctaError ?? "Deposit"}
            </AppButton>
        </Column>
    );
});

function useAddLiquidity() {
    const {
        addLiquidityQuote,
        loanExpanded,
        reset,
        slippageController: { isAutoSlippage }
    } = useWhirlpoolContext();
    const send = useTransactionSender();
    const increaseLiquidity = useWhirlpoolIncreaseLiquidityTransaction();
    const zcLedger = getZcLedger(loanExpanded);
    return async () => {
        if (!addLiquidityQuote || !zcLedger) return Result.errFromMessage(LOADING_ERROR);

        const strategyIdentifier = getLoanStrategyIdentifier(loanExpanded);
        if (!strategyIdentifier) return Result.errFromMessage("Updates are not available for this loan");

        const positionMint = getZcCollateral(loanExpanded)?.assetMint;
        if (!positionMint) return Result.errFromMessage("No position mint found");

        const params: GetUpdateLiquidityTxnArgs = {
            loan: loanExpanded.loan.address,
            increase: true,
            positionMint,
            manageParams: {
                collateralIndex: 0,
                liquidityAmount: addLiquidityQuote.liquidity.toNumber(),
                transferParams: isAutoSlippage
                    ? { Liquidity: { slippageToleranceBps: decimalsToBps(0.1, "bps") } }
                    : {
                          TokenAmounts: {
                              tokenAAmount: addLiquidityQuote.maxTokenA.toNumber(),
                              tokenBAmount: addLiquidityQuote.maxTokenB.toNumber()
                          }
                      }
            }
        };

        return await send(increaseLiquidity, params, { sendOptions: { onSuccess: reset } });
    };
}
